import MultiCall_ABI from './abi/Multicall_abi.json';
import { AccountInterface, Call, ProviderInterface, transaction, Contract } from 'starknet';
import { Nullable } from '../../interfaces';
import { Uint256 } from '../../datastructures';

/**
 * Maximum number of calls per single multicall RPC request.
 * Large multicalls can exceed RPC node payload/computation limits,
 * causing "Failed to fetch" network errors. Batching keeps each
 * request within safe limits.
 */
const MULTICALL_BATCH_SIZE = 50;

/** Number of retry attempts for transient RPC failures (network errors, 429s). */
const MAX_RETRIES = 2;

/** Base delay between retries in ms — doubled on each subsequent attempt. */
const RETRY_BASE_DELAY_MS = 500;

function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Thrown when the on-chain multicall reverts due to a sub-call contract error
 * (e.g. oracle returning zero → division by zero). Retrying or switching RPC
 * providers will not help — the error is deterministic.
 */
export class ContractRevertError extends Error {
  constructor(message: string, public readonly cause?: unknown) {
    super(message);
    this.name = 'ContractRevertError';
  }
}

function isContractRevert(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message;
  return msg.includes('revert_error') || msg.includes('Contract error') || msg.includes('Assertion failed');
}

export class MultiCallContract {
  private contract: Contract;

  constructor(address: string, providerOrAccount: ProviderInterface | AccountInterface) {
    this.contract = new Contract({ abi: MultiCall_ABI, address, providerOrAccount });
  }

  async aggregate(calls: Call[]): Promise<Nullable<bigint[]>> {
    if (calls.length === 0) {
      return [];
    }

    if (calls.length <= MULTICALL_BATCH_SIZE) {
      return this.executeBatch(calls);
    }

    // Split calls into batches to avoid oversized RPC requests
    const batches: Call[][] = [];
    for (let i = 0; i < calls.length; i += MULTICALL_BATCH_SIZE) {
      batches.push(calls.slice(i, i + MULTICALL_BATCH_SIZE));
    }

    const results = await Promise.all(batches.map(batch => this.executeBatch(batch)));

    if (results.some(result => result === null)) {
      return null;
    }

    return (results as bigint[][]).flat();
  }

  private async executeBatch(calls: Call[]): Promise<Nullable<bigint[]>> {
    let callArray: ReturnType<typeof transaction.transformCallsToMulticallArrays>['callArray'];
    let calldataWithoutLengthItem: string[];

    try {
      const transformed = transaction.transformCallsToMulticallArrays(calls);
      callArray = transformed.callArray;
      // First item in calldata is the length of the array - we don't want to send it to the contract
      calldataWithoutLengthItem = transformed.calldata.slice(1);
    } catch (error) {
      console.warn('Multicall: failed to prepare call data', error);
      return null;
    }

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const result = await this.contract.aggregate(callArray, calldataWithoutLengthItem);

        return result.retdata;
      } catch (error) {
        if (isContractRevert(error)) {
          throw new ContractRevertError(
            'Multicall reverted due to a sub-call contract error (likely a broken oracle)',
            error,
          );
        }

        const isLastAttempt = attempt === MAX_RETRIES;

        if (isLastAttempt) {
          console.warn(`Multicall aggregate failed after ${MAX_RETRIES + 1} attempts:`, error);
          return null;
        }

        const backoff = RETRY_BASE_DELAY_MS * 2 ** attempt;
        console.warn(`Multicall aggregate attempt ${attempt + 1} failed, retrying in ${backoff}ms...`, error);
        await delay(backoff);
      }
    }

    return null;
  }

  async aggregateUint256ArrayAsync(calls: Call[]): Promise<Nullable<Uint256[]>> {
    const result = await this.aggregate(calls);
    if (!result) {
      return null;
    }

    const resultData = result.filter((_, index) => index % 3 !== 0); // remove uint256 length indicator
    const lowUint256 = resultData.filter((_, index) => index % 2 === 0);
    const highUint256 = resultData.filter((_, index) => index % 2 === 1);

    return lowUint256.map((low, index) => {
      const high = highUint256[index];
      return { low, high } as Uint256;
    });
  }
}
